import { ThreadAnalysisResultItem } from "@coeff/api";
import { Collapse, Empty, Table, Typography } from "antd";
import React from "react";
import styled from "styled-components";

const { Text } = Typography;

// result is free-form JSON — its shape is owned by the analysis prompt.
type AnalysisResult = NonNullable<ThreadAnalysisResultItem["result"]>;

const CodeBlock = styled.pre`
  margin: 8px 0 0;
  padding: 8px 10px;
  background: #f6f8fa;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  color: #333;
  font-size: 12px;
  max-height: 320px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
`;

const SectionTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #444;
  margin: 12px 0 6px;
`;

function formatCell(value: unknown): string {
  if (value === null || value === undefined) {
    return "";
  }
  if (typeof value === "object") {
    return JSON.stringify(value);
  }
  return String(value);
}

interface ResultViewProps {
  result?: AnalysisResult;
}

const ArraySection: React.FC<{ name: string; items: unknown[] }> = ({ name, items }) => {
  const objectItems = items.filter(
    (it): it is Record<string, unknown> =>
      typeof it === "object" && it !== null && !Array.isArray(it)
  );
  const useTable = objectItems.length === items.length && items.length > 0;

  const columnKeys: string[] = [];
  if (useTable) {
    objectItems.forEach(it => {
      Object.keys(it).forEach(k => {
        if (!columnKeys.includes(k)) {
          columnKeys.push(k);
        }
      });
    });
  }

  const columns = columnKeys.map(k => ({
    title: k,
    dataIndex: k,
    key: k,
    render: (value: unknown) => (
      <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 12 }}>
        {formatCell(value)}
      </div>
    ),
  }));

  return (
    <>
      <SectionTitle>
        {name} ({items.length})
      </SectionTitle>
      {useTable ? (
        <Table
          dataSource={objectItems.map((it, i) => ({ _key: i, ...it }))}
          columns={columns}
          rowKey="_key"
          size="small"
          pagination={false}
          bordered
        />
      ) : items.length === 0 ? (
        <Text type="secondary">No items.</Text>
      ) : (
        <CodeBlock>{JSON.stringify(items, null, 2)}</CodeBlock>
      )}
    </>
  );
};

// Schema-agnostic renderer: EVERY top-level array becomes its own table (one row per item, union
// of item keys as columns); everything else is shown as raw JSON. Works for any analysis prompt —
// the daily digest emits two arrays (struggles, one_turn_issues), older prompts emit one.
export const ResultView: React.FC<ResultViewProps> = ({ result }) => {
  if (!result || Object.keys(result).length === 0) {
    return <Empty description="Empty result (no items)" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }

  const arrayKeys = Object.keys(result).filter(k => Array.isArray(result[k]));
  const rest: AnalysisResult = {};
  Object.keys(result).forEach(k => {
    if (!arrayKeys.includes(k)) {
      rest[k] = result[k];
    }
  });

  return (
    <div>
      {arrayKeys.map(key => (
        <ArraySection key={key} name={key} items={result[key] as unknown[]} />
      ))}

      {Object.keys(rest).length > 0 && (
        <Collapse
          ghost
          defaultActiveKey={arrayKeys.length ? [] : ["rest"]}
          style={{ marginTop: 8 }}
        >
          <Collapse.Panel header="Other result fields" key="rest">
            <CodeBlock>{JSON.stringify(rest, null, 2)}</CodeBlock>
          </Collapse.Panel>
        </Collapse>
      )}

      <Collapse ghost defaultActiveKey={[]} style={{ marginTop: 4 }}>
        <Collapse.Panel header="Raw result JSON" key="raw">
          <CodeBlock>{JSON.stringify(result, null, 2)}</CodeBlock>
        </Collapse.Panel>
      </Collapse>
    </div>
  );
};
